import { FC } from 'react';
import type { DrePayload } from '@/types/dashboards';
import { fmtMil, type LinhaDesvio } from '@/utils/equiplexDre';
import { fmtBRL, fmtDesvio } from '@/utils/painelFormat';
import { visualFaixaOrcado } from '../chartTheme';
import PainelCard from '../PainelCard';

interface Props {
  linhas: LinhaDesvio[];
  mesLabel: string;
  regras: DrePayload['regras'];
  temOrcado: boolean;
  /** Contábil do mês ainda sendo lançado: "abaixo do orçado" pode ser só lançamento faltando. */
  parcial?: boolean;
}

const Situacao: FC<{ linha: LinhaDesvio }> = ({ linha }) => {
  const v = visualFaixaOrcado(linha.faixa);
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-content-primary">
      <span aria-hidden className="material-symbols-outlined text-[16px] leading-none" style={{ color: v.cor }}>
        {v.icone}
      </span>
      {v.rotulo}
    </span>
  );
};

/**
 * Orçado × realizado do mês, do desvio mais grave para o mais tranquilo. Só entra o que o
 * orçamento do Datasul (BGC) orça: despesas e custo de produção por natureza.
 */
const DesviosOrcadoTable: FC<Props> = ({ linhas, mesLabel, regras, temOrcado, parcial = false }) => (
  <PainelCard
    testId="dre-desvios"
    titulo={`Orçado × realizado — ${mesLabel}`}
    subtitulo={`${
      parcial ? 'Mês parcial: o contábil ainda está sendo lançado, então "abaixo do orçado" pode ser só lançamento faltando. ' : ''
    }Orçamento contábil do Datasul (BGC). Atenção acima de ${regras.faixa_atencao_pct}% do orçado, crítico acima de ${regras.faixa_critica_pct}%; desvio menor que ${fmtBRL(regras.materialidade)} não vira alerta.`}
  >
    {!temOrcado || !linhas.length ? (
      <p className="text-sm text-content-muted">Nenhum orçamento da Equiplex no Datasul para este mês.</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm tabular-nums">
          <thead>
            <tr className="border-b border-theme-border text-xs uppercase tracking-wide text-content-muted">
              <th className="py-2 pr-3 text-left font-medium">Linha</th>
              <th className="px-2 py-2 text-right font-medium">Realizado</th>
              <th className="px-2 py-2 text-right font-medium">Orçado</th>
              <th className="px-2 py-2 text-right font-medium">Desvio (R$ mil)</th>
              <th className="px-2 py-2 text-right font-medium">Desvio</th>
              <th className="py-2 pl-2 text-left font-medium">Situação</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.key} className="border-b border-theme-border/50">
                <th scope="row" className="py-1.5 pr-3 text-left font-normal text-content-primary">
                  {l.rotulo}
                  <span className="ml-2 text-xs text-content-muted">{l.grupo}</span>
                </th>
                <td className="px-2 py-1.5 text-right">{fmtMil(l.realizado)}</td>
                <td className="px-2 py-1.5 text-right">{fmtMil(l.orcado)}</td>
                <td className="px-2 py-1.5 text-right">{fmtMil(l.desvio)}</td>
                <td className="px-2 py-1.5 text-right">{fmtDesvio(l.desvioPct)}</td>
                <td className="py-1.5 pl-2">
                  <Situacao linha={l} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-content-muted">Valores em R$ mil com o sinal do contábil: gastar mais que o orçado dá desvio negativo.</p>
      </div>
    )}
  </PainelCard>
);

export default DesviosOrcadoTable;
